import React, { useEffect, useMemo } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { MapContainer, TileLayer, CircleMarker, Circle, Popup, Tooltip as MapTooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fmtMoney } from '../../Documents/format';
import { fmtInt, fmtQty } from '../../OnlineJournal/format';
import { LatLng, Origin, PharmacyRow, fmtDistance, hasCoords } from '../remainsCalc';
import { BORDER, OPEN_COLORS, openKey, panelSx } from '../remainsStyles';
import { openLabel } from './RemainsCommon';

interface Props {
  rows: PharmacyRow[];
  origin: Origin | null;
  radius: number | null;
  withSums: boolean;
  onOpen: (guid: string) => void;
}

/** Шымкент - центр, пока на карте нет ни одной точки */
const DEFAULT_CENTER: [number, number] = [42.3417, 69.5901];
const ORIGIN_COLOR = '#dc2626';
const MAP_HEIGHT = 520;

interface MapPoint extends LatLng { row: PharmacyRow }

/** Подгоняет масштаб под аптеки и точку отсчёта; повторно - только когда меняется набор точек */
const FitBounds: React.FC<{ points: LatLng[]; signature: string }> = ({ points, signature }) => {
  const map = (useMap as any)();
  useEffect(() => {
    if (!map || !points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 15);
      return;
    }
    map.fitBounds(L.latLngBounds(points.map(p => [p.lat, p.lng] as [number, number])), { padding: [32, 32], maxZoom: 15 });
    // точки описывает signature
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, signature]);
  return null;
};

/** Размер маркера растёт с количеством товара в аптеке (по корню, чтобы крупные аптеки не перекрывали остальные) */
function markerRadius(units: number, max: number): number {
  if (max <= 0) return 7;
  return 6 + Math.round(8 * Math.sqrt(units / max));
}

/** Аптеки с остатком на карте: цвет - открыта / закрыта / режим работы не указан */
const RemainsMap: React.FC<Props> = ({ rows, origin, radius, withSums, onOpen }) => {
  const points = useMemo<MapPoint[]>(() => {
    const out: MapPoint[] = [];
    rows.forEach(r => {
      if (hasCoords(r.pharmacy)) out.push({ lat: r.pharmacy.lat, lng: r.pharmacy.lng, row: r });
    });
    return out;
  }, [rows]);
  const missing = rows.length - points.length;
  const maxUnits = points.reduce((m, p) => Math.max(m, p.row.units), 0);

  const fitPoints = useMemo<LatLng[]>(() => {
    const list: LatLng[] = points.map(p => ({ lat: p.lat, lng: p.lng }));
    if (origin) list.push({ lat: origin.lat, lng: origin.lng });
    return list;
  }, [points, origin]);
  const signature = useMemo(
    () => fitPoints.map(p => `${p.lat.toFixed(4)},${p.lng.toFixed(4)}`).sort().join('|'),
    [fitPoints]
  );

  return (
    <Paper variant="outlined" sx={panelSx}>
      <Box sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', borderBottom: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontSize: '0.8rem', color: '#475569' }}>
          На карте <Box component="b" sx={{ color: '#0f172a' }}>{fmtInt(points.length)}</Box> из {fmtInt(rows.length)}
          {missing > 0 && <Box component="span" sx={{ color: '#b45309' }}> · без координат: {fmtInt(missing)}</Box>}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {[
          { key: 'open' as const, label: 'Открыта' },
          { key: 'closed' as const, label: 'Закрыта' },
          { key: 'unknown' as const, label: 'Режим не указан' }
        ].map(l => (
          <Box key={l.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: OPEN_COLORS[l.key].marker }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>{l.label}</Typography>
          </Box>
        ))}
        {origin && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: ORIGIN_COLOR }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Точка отсчёта</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ height: MAP_HEIGHT, '& .leaflet-container': { height: '100%', fontFamily: 'inherit' } }}>
        <MapContainer center={DEFAULT_CENTER} zoom={12} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds points={fitPoints} signature={signature} />

          {origin && radius && (
            <Circle
              center={[origin.lat, origin.lng]} radius={radius * 1000}
              pathOptions={{ color: ORIGIN_COLOR, weight: 1, fillColor: ORIGIN_COLOR, fillOpacity: 0.04, dashArray: '4 4' }}
            />
          )}
          {origin && (
            <CircleMarker
              center={[origin.lat, origin.lng]} radius={7}
              pathOptions={{ color: '#fff', weight: 2, fillColor: ORIGIN_COLOR, fillOpacity: 1 }}
            >
              <MapTooltip direction="top" offset={[0, -6]}>{origin.label}</MapTooltip>
            </CircleMarker>
          )}

          {points.map(({ lat, lng, row }) => {
            const p = row.pharmacy;
            const color = OPEN_COLORS[openKey(p.open)].marker;
            const state = openLabel(p);
            return (
              <CircleMarker
                key={p.guid} center={[lat, lng]} radius={markerRadius(row.units, maxUnits)}
                pathOptions={{ color: '#fff', weight: 1.5, fillColor: color, fillOpacity: 0.85 }}
              >
                <MapTooltip direction="top" offset={[0, -6]}>{p.name} · {fmtQty(row.units)} уп.</MapTooltip>
                <Popup minWidth={220}>
                  <Box sx={{ fontFamily: 'inherit' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{p.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#475569', mt: 0.25 }}>{p.address || 'Адрес не указан'}</Typography>
                    <Typography sx={{ fontSize: 12, color: '#475569', mt: 0.5 }}>
                      {p.hours || 'Режим работы не указан'}
                      {state && <Box component="span" sx={{ display: 'block', color: OPEN_COLORS[openKey(p.open)].color, fontWeight: 600 }}>{state}</Box>}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#0f172a', mt: 0.5 }}>
                      {fmtInt(row.positions)} поз. · <b>{fmtQty(row.units)} уп.</b>
                      {withSums && row.amount !== null && <> · {fmtMoney(row.amount)}</>}
                      {row.distance !== null && <> · {fmtDistance(row.distance)}</>}
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => onOpen(p.guid)} sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}>
                      Карточка аптеки
                    </Button>
                  </Box>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </Box>
    </Paper>
  );
};

export default React.memo(RemainsMap);
